import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  message,
  Space,
  Typography,
  Spin,
} from "antd";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MenuOutlined, SaveOutlined, ReloadOutlined } from "@ant-design/icons";
import { supabase } from "../../lib/supabase";
import {
  fetchCostOverviewDisplayOrder,
  saveCostOverviewDisplayOrder,
  type CostOverviewOrderItem,
} from "../../services/costOverviewDisplayOrderService";

const { Title, Text } = Typography;

// Fixed system items: always in cost overview when applicable (Transport from tariff, LMD, etc.)
const FIXED_KEYS = [
  { key: "transport", label: "Transport Cost (from tariff)" },
  {
    key: "containervervoer_plaats_van_bestemming",
    label: "Containervervoer plaats van bestemming",
  },
  { key: "export_clearance", label: "Export Clearance License" },
  { key: "pickup_fee", label: "Pickup Fee" },
  { key: "commodity_inspection", label: "Commodity Inspection" },
  { key: "post_export_license", label: "Post Export License" },
  { key: "customs_btw", label: "BTW bij Invoer" },
  { key: "customs_duties", label: "Invoerrechten" },
  { key: "customs_commission", label: "Voorschotprovisie BTW & Invoerrechten" },
  { key: "lmd", label: "LMD (Last Mile Delivery)" },
  { key: "lmd_surcharge", label: "LMD Diesel Surcharge" },
  { key: "lmd_nl_pallet_surcharge", label: "Tol surcharge" },
] as const;

const SortableItem = ({
  item,
  label,
}: {
  item: CostOverviewOrderItem;
  label: string;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.item_key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    cursor: "move",
    background: "#fff",
    border: "1px solid #f0f0f0",
    borderRadius: 8,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Space>
        <MenuOutlined style={{ color: "#999", marginRight: 8 }} />
        <Text>{label}</Text>
      </Space>
    </div>
  );
};

const CostOverviewOrderTab: React.FC = () => {
  const [items, setItems] = useState<CostOverviewOrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadItems = async () => {
    setLoading(true);
    try {
      const [orderRows, { data: additionalCosts }, { data: pricingComponents }] =
        await Promise.all([
          fetchCostOverviewDisplayOrder(),
          supabase
            .from("additional_costs")
            .select("id, name")
            .eq("is_active", true)
            .order("name"),
          supabase
            .from("pricing_components")
            .select("id, name")
            .order("name"),
        ]);

      const orderMap = new Map(
        orderRows.map((r) => [r.item_key, r.sort_order]),
      );
      const DEFAULT_ORDER = 9999;

      const allItems: CostOverviewOrderItem[] = [];

      const defaultFixedSortOrder = (key: string, fallback: number): number => {
        if (key === "lmd_nl_pallet_surcharge") {
          const dieselOrder = orderMap.get("lmd_surcharge");
          if (dieselOrder != null) return dieselOrder + 1;
          const lmdOrder = orderMap.get("lmd");
          if (lmdOrder != null) return lmdOrder + 1;
        }
        return fallback;
      };

      for (const fixed of FIXED_KEYS) {
        const existing = orderRows.find((r) => r.item_key === fixed.key);
        allItems.push({
          item_key: fixed.key,
          display_label: fixed.label,
          sort_order:
            existing?.sort_order ??
            orderMap.get(fixed.key) ??
            defaultFixedSortOrder(
              fixed.key,
              DEFAULT_ORDER + allItems.length,
            ),
        });
      }

      const acList = (additionalCosts || []) as Array<{ id: number; name: string }>;
      for (const ac of acList) {
        const key = `additional_cost_${ac.id}`;
        const existing = orderRows.find((r) => r.item_key === key);
        allItems.push({
          item_key: key,
          display_label: ac.name,
          sort_order: existing?.sort_order ?? orderMap.get(key) ?? DEFAULT_ORDER + allItems.length,
        });
      }

      const pcList = (pricingComponents || []) as Array<{ id: number; name: string }>;
      for (const pc of pcList) {
        const key = `pricing_component_${pc.id}`;
        const existing = orderRows.find((r) => r.item_key === key);
        allItems.push({
          item_key: key,
          display_label: pc.name,
          sort_order: existing?.sort_order ?? orderMap.get(key) ?? DEFAULT_ORDER + allItems.length,
        });
      }

      // Include DB-only system keys (e.g. from migrations) not covered above
      const knownKeys = new Set(allItems.map((item) => item.item_key));
      for (const row of orderRows) {
        if (!knownKeys.has(row.item_key)) {
          allItems.push({
            item_key: row.item_key,
            display_label: row.display_label || row.item_key,
            sort_order: row.sort_order,
          });
        }
      }

      allItems.sort((a, b) => a.sort_order - b.sort_order);
      setItems(allItems);
    } catch (e) {
      console.error("Error loading cost overview order:", e);
      message.error("Failed to load display order");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setItems((prev) => {
      const arr = [...prev];
      const fromIdx = arr.findIndex((i) => i.item_key === active.id);
      const toIdx = arr.findIndex((i) => i.item_key === over.id);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [removed] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, removed);
      return arr.map((it, idx) => ({ ...it, sort_order: idx }));
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const toSave = items.map((it, idx) => ({
        ...it,
        sort_order: idx,
      }));
      const { success, error } = await saveCostOverviewDisplayOrder(toSave);
      if (success) {
        message.success("Display order saved");
      } else {
        message.error(error || "Failed to save");
      }
    } catch (e) {
      message.error("Failed to save display order");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }

  return (
    <Card>
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Title level={5} style={{ margin: 0 }}>
            Kostenoverzicht volgorde
          </Title>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadItems()}
              loading={loading}
            >
              Vernieuwen
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              Opslaan
            </Button>
          </Space>
        </div>
        <Text type="secondary" style={{ display: "block" }}>
          Sleep de items om de volgorde in het kostenoverzicht van de rekenmachine te bepalen.
          Nieuwe componenten en aanvullende kosten verschijnen automatisch in deze lijst; klik op Vernieuwen om ze te laden.
          Transport Cost komt uit het tarief (geen component). Containervervoer, LMD, Export Clearance en Pickup Fee zijn vaste systeemitems.
        </Text>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((i) => i.item_key)}
            strategy={verticalListSortingStrategy}
          >
            {items.map((item) => (
              <SortableItem
                key={item.item_key}
                item={item}
                label={item.display_label || item.item_key}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Space>
    </Card>
  );
};

export default CostOverviewOrderTab;
